import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { BUTTON_QUIET, RequestList, RequestThread } from "../../components/helpdesk/HelpDeskViews";
import {
  addReply,
  friendlyError,
  listAllTickets,
  listMessages,
  markUrgent,
  screenshotUrl,
  setStatus,
  STATUSES,
  STATUS_LABELS,
} from "../../lib/helpDesk/helpDesk.mjs";
import type { HelpMessage, HelpStatus, HelpTicket } from "../../lib/helpDesk/helpDesk.mjs";
import { getSupabase } from "../../lib/supabase";

/**
 * Admin panel: Help desk (issue #1096). Every customer's requests (staff RLS,
 * migration 0110), filtered by status. Meridian replies and sets the status
 * here; both are stored on the ticket, and the requester sees them under My
 * requests. The GitHub issue the droplet opened is linked; a later sync of
 * replies and status to that issue is a follow-up.
 */
export default function AdminHelpDesk() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("ticket");
  const [filter, setFilter] = useState<HelpStatus | "active" | "all">("active");
  const [tickets, setTickets] = useState<HelpTicket[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [shotHref, setShotHref] = useState<string | null>(null);

  const load = useCallback(async () => {
    const status = filter === "active" || filter === "all" ? null : filter;
    const { tickets: rows, error } = await listAllTickets(getSupabase(), { status });
    setLoadError(error ? friendlyError(error) : null);
    setTickets(filter === "active" ? rows.filter((t) => t.status !== "resolved") : rows);
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = tickets?.find((t) => t.id === selectedId) ?? null;

  const loadThread = useCallback(async () => {
    if (!selected) {
      setMessages([]);
      return;
    }
    const { messages: rows } = await listMessages(getSupabase(), {
      tenantId: selected.tenant_id,
      ticketId: selected.id,
    });
    setMessages(rows);
  }, [selected]);

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  const shotPath = selected?.screenshot_path ?? null;
  useEffect(() => {
    let alive = true;
    setShotHref(null);
    if (shotPath) {
      void screenshotUrl(getSupabase(), shotPath).then((url) => alive && setShotHref(url));
    }
    return () => {
      alive = false;
    };
  }, [shotPath]);

  function select(id: string) {
    const next = new URLSearchParams(params);
    next.set("ticket", id);
    setParams(next, { replace: true });
    setDraft("");
    setActionError(null);
  }

  async function sendReply() {
    if (!selected) return;
    setSending(true);
    setActionError(null);
    const { error } = await addReply(getSupabase(), { ticketId: selected.id, body: draft });
    setSending(false);
    if (error) {
      setActionError(friendlyError(error));
      return;
    }
    setDraft("");
    await load();
    await loadThread();
  }

  async function changeStatus(status: HelpStatus) {
    if (!selected) return;
    setActionError(null);
    const { error } = await setStatus(getSupabase(), selected.id, status);
    if (error) setActionError(friendlyError(error));
    await load();
  }

  async function urgent() {
    if (!selected) return;
    const { error } = await markUrgent(getSupabase(), selected.id);
    if (error) setActionError(friendlyError(error));
    await load();
  }

  const staffControls = selected ? (
    <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontWeight: 600 }}>Status</span>
        <select
          value={selected.status}
          onChange={(e) => void changeStatus(e.target.value as HelpStatus)}
          style={{ font: "inherit", padding: "4px 8px" }}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </label>
      <div style={{ color: "var(--text-2)" }}>
        {selected.tenant?.name ?? "Customer"} · {selected.requester_email ?? "no email"}
        {selected.browser ? ` · ${selected.browser}` : ""}
      </div>
      <div style={{ color: "var(--text-2)" }}>
        {selected.github_issue_url ? (
          <a href={selected.github_issue_url} target="_blank" rel="noopener noreferrer">
            GitHub issue #{selected.github_issue_number}
          </a>
        ) : (
          "GitHub issue not opened yet (the droplet opens it within a minute)."
        )}
      </div>
      {selected.notify_error ? (
        <div style={{ color: "var(--flag-red)" }}>
          Last notification error: {selected.notify_error}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <main style={{ padding: "24px 28px", display: "grid", gap: 16 }}>
      <header style={{ display: "grid", gap: 8 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Help desk</h1>
        <div
          role="group"
          aria-label="Filter by status"
          style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
        >
          {(["active", ...STATUSES, "all"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
              style={{
                ...BUTTON_QUIET,
                padding: "4px 12px",
                fontSize: 12,
                background: filter === value ? "var(--domain-disposal-wash)" : "transparent",
              }}
            >
              {value === "active" ? "Not resolved" : value === "all" ? "All" : STATUS_LABELS[value]}
            </button>
          ))}
        </div>
      </header>
      {loadError ? (
        <div role="alert" style={{ color: "var(--flag-red)", fontSize: 13 }}>
          {loadError}
        </div>
      ) : null}
      {tickets == null ? (
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>Loading...</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 380px) minmax(0, 1fr)",
            gap: 20,
            alignItems: "start",
          }}
        >
          <RequestList
            tickets={tickets}
            selectedId={selectedId}
            showRequester
            showCustomer
            onSelect={select}
          />
          {selected ? (
            <RequestThread
              ticket={selected}
              messages={messages}
              replyDraft={draft}
              sending={sending}
              error={actionError}
              canMarkUrgent
              screenshotHref={shotHref}
              staffControls={staffControls}
              asMeridian
              onReplyChange={setDraft}
              onSendReply={() => void sendReply()}
              onMarkUrgent={() => void urgent()}
            />
          ) : (
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>Pick a request.</div>
          )}
        </div>
      )}
    </main>
  );
}
